import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Modal, Form, Input, Popconfirm, Card, Switch } from 'antd';
import { TrInputNumber as InputNumber } from '../components/TrInputNumber';
import { PlusOutlined, EditOutlined, DeleteOutlined, ToolOutlined } from '@ant-design/icons';
import {
  fetchServiceItems,
  createServiceItem,
  updateServiceItem,
  deleteServiceItem,
  type ServiceItemDto
} from '../services/serviceOrderService';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useCanDelete } from '../hooks/useCanDelete';

const ServiceItems: React.FC = () => {
  const isMobile = useBreakpoint();
  const canDelete = useCanDelete();
  const [items, setItems] = useState<ServiceItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<ServiceItemDto | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await fetchServiceItems();
      setItems(data);
    } catch (error) {
      message.error('Hizmet kalemleri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ defaultPrice: 0, isActive: true });
    setModalVisible(true);
  };

  const handleEdit = (item: ServiceItemDto) => {
    setEditingItem(item);
    form.setFieldsValue({
      name: item.name,
      description: item.description,
      smsText: item.smsText,
      defaultPrice: item.defaultPrice,
      intervalKm: item.intervalKm,
      intervalMonths: item.intervalMonths,
      isActive: item.isActive
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingItem) {
        await updateServiceItem(editingItem.id, {
          name: values.name,
          description: values.description,
          smsText: values.smsText,
          defaultPrice: values.defaultPrice || 0,
          intervalKm: values.intervalKm,
          intervalMonths: values.intervalMonths,
          isActive: values.isActive !== undefined ? values.isActive : true
        });
        message.success('Hizmet kalemi güncellendi');
      } else {
        await createServiceItem({
          name: values.name,
          description: values.description,
          smsText: values.smsText,
          defaultPrice: values.defaultPrice || 0,
          intervalKm: values.intervalKm,
          intervalMonths: values.intervalMonths
        });
        message.success('Hizmet kalemi eklendi');
      }
      
      setModalVisible(false);
      form.resetFields();
      loadItems();
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('İşlem sırasında hata oluştu');
      }
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteServiceItem(id);
      message.success('Hizmet kalemi silindi');
      loadItems();
    } catch (error: any) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Hizmet kalemi silinirken hata oluştu');
      }
    }
  };

  const columns = [
    {
      title: 'Hizmet Adı',
      dataIndex: 'name',
      key: 'name'},
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || '-'},
    {
      title: 'SMS Metni',
      dataIndex: 'smsText',
      key: 'smsText',
      render: (text: string) => text || '-'},
    {
      title: 'Varsayılan Fiyat',
      dataIndex: 'defaultPrice',
      key: 'defaultPrice',
      render: (price: number) => `${price.toFixed(2)} ₺`},
    {
      title: 'Bakım Aralığı (Km)',
      dataIndex: 'intervalKm',
      key: 'intervalKm',
      render: (km: number) => km ? `${km.toLocaleString()} km` : '-'},
    {
      title: 'Bakım Aralığı (Ay)',
      dataIndex: 'intervalMonths',
      key: 'intervalMonths',
      render: (months: number) => months ? `${months} ay` : '-'},
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <span style={{ color: isActive ? 'green' : 'red' }}>
          {isActive ? 'Aktif' : 'Pasif'}
        </span>
      )},
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: ServiceItemDto) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Düzenle
          </Button>
          {canDelete && (
          <Popconfirm
            title="Bu hizmet kalemini silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Sil
            </Button>
          </Popconfirm>
          )}
        </Space>
      )},
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>
          <ToolOutlined /> Hizmet Kalemleri
        </h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          Hizmet Kalemi Ekle
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          scroll={{ x: 500 }}
          pagination={{
            pageSize: 20,
            showSizeChanger: !isMobile,
            showTotal: (total) => `Toplam ${total} hizmet kalemi`}}
        />
      </Card>

      <Modal
        title={editingItem ? 'Hizmet Kalemini Düzenle' : 'Yeni Hizmet Kalemi Ekle'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingItem(null);
        }}
        okText="Kaydet"
        cancelText="İptal"
        width={isMobile ? '100%' : 600}
        style={isMobile ? { maxWidth: '100%', top: 16 } : undefined}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Hizmet Adı"
            rules={[{ required: true, message: 'Lütfen hizmet adı girin' }]}
          >
            <Input placeholder="Örn: Motor Tamiri, Fren Bakımı" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Açıklama"
          >
            <Input.TextArea rows={3} placeholder="Hizmet açıklaması" />
          </Form.Item>
          <Form.Item
            name="smsText"
            label="SMS Metni"
          >
            <Input.TextArea rows={2} placeholder="SMS metni" maxLength={500} />
          </Form.Item>
          <Form.Item
            name="defaultPrice"
            label="Varsayılan Fiyat"
            rules={[{ required: true, message: 'Lütfen varsayılan fiyat girin' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              precision={2}
              placeholder="0,00"
            />
          </Form.Item>
          <Form.Item
            name="intervalKm"
            label="Periyodik Bakım Aralığı (Km)"
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              integerOnly
              placeholder="Örn: 10000"
            />
          </Form.Item>
          <Form.Item
            name="intervalMonths"
            label="Periyodik Bakım Aralığı (Ay)"
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              integerOnly
              placeholder="Örn: 6"
            />
          </Form.Item>
          {editingItem && (
            <Form.Item
              name="isActive"
              label="Aktif"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default ServiceItems;
